import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import BlockIcon from '@mui/icons-material/Block';
import HistoryIcon from '@mui/icons-material/History';
import { Box, Drawer, IconButton, Typography } from '@mui/material';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { IClosedLetterRequest, IClosedLetterRequestAudit } from '../../../models/Inquiry/LetterDetails/IClosedLetterRequest';
import { usePost } from '../../../utils/apiHelper';

interface ViewAuditDrawerProps {
    open: boolean;
    request: IClosedLetterRequest | null;
    onClose: () => void;
}

const ink = '#16233a';
const paper = '#f6f7f4';
const accent = '#3949ab';
const line = '#dfe3de';
const serif = "'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, serif";
const mono = "'IBM Plex Mono', 'SFMono-Regular', Consolas, monospace";

const actionMeta: Record<string, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
    Downloaded: { label: 'Downloaded', color: '#0d47a1', bg: '#e3f2fd', icon: <CloudDownloadIcon fontSize="small" /> },
    Emailed: { label: 'Emailed', color: '#1b5e20', bg: '#e8f5e9', icon: <EmailOutlinedIcon fontSize="small" /> },
    Mailed: { label: 'Mailed', color: '#00695c', bg: '#e0f2f1', icon: <TaskAltOutlinedIcon fontSize="small" /> },
    Rejected: { label: 'Rejected', color: '#b71c1c', bg: '#fdecea', icon: <BlockIcon fontSize="small" /> },
};

const ViewAuditDrawer: React.FC<ViewAuditDrawerProps> = ({ open, request, onClose }) => {
    const [entries, setEntries] = useState<IClosedLetterRequestAudit[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open && request) {
            setLoading(true);
            usePost<IClosedLetterRequestAudit[]>('ClosedLetterRequestAuditGet', { requestId: request.id })
                .then((response) => setEntries(response.data || []))
                .finally(() => setLoading(false));
        } else {
            setEntries([]);
        }
    }, [open, request]);

    return (
        <Drawer
            open={open}
            anchor="right"
            onClose={onClose}
            PaperProps={{ sx: { boxShadow: '-8px 0 32px rgba(22, 35, 58, 0.16)', height: '100vh', maxHeight: '100vh', overflow: 'hidden' } }}
        >
            <Box
                display="flex"
                flexDirection="column"
                height="100%"
                width={640}
                maxWidth="95vw"
                bgcolor={paper}
                sx={{ fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif", overflow: 'hidden' }}
            >
                <Box
                    flexShrink={0}
                    sx={{
                        position: 'relative',
                        backgroundColor: '#007FFF',
                        color: '#fff',
                        padding: '16px 30px 14px',
                    }}
                >
                    <Box display="flex" alignItems="center" gap={1.25} mb={0.25}>
                        <HistoryIcon sx={{ fontSize: 18, color: '#d6ecff' }} />
                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#d6ecff' }}>
                            Closed Letter Requests
                        </Typography>
                    </Box>
                    <Typography sx={{ fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif", fontSize: '1.45rem', lineHeight: 1.15, fontWeight: 500 }}>
                        Audit History
                    </Typography>
                    <IconButton
                        onClick={onClose}
                        sx={{ position: 'absolute', right: 12, top: 10, color: 'rgba(255,255,255,0.75)', '&:hover': { color: '#fff' } }}
                    >
                        <HighlightOffIcon style={{ fontSize: 24 }} />
                    </IconButton>
                </Box>

                {request && (
                    <Box padding="10px 30px" sx={{ backgroundColor: '#fff', borderBottom: `1px solid ${line}` }}>
                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8a9088' }}>
                            Regarding
                        </Typography>
                        <Box display="flex" alignItems="baseline" gap={1} flexWrap="wrap">
                            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: ink }}>
                                {request.letterName}
                            </Typography>
                            <Typography sx={{ fontSize: '0.76rem', color: '#5b625b', fontFamily: mono }}>
                                Debtor {request.debtorNumber} · Client {request.clientId}/{request.clientAccountNumber}
                            </Typography>
                        </Box>
                    </Box>
                )}

                <Box flexGrow={1} overflow="auto" padding="18px 30px">
                    {loading && (
                        <Typography sx={{ fontSize: '0.85rem', color: '#8a9088' }}>Loading history…</Typography>
                    )}

                    {!loading && entries.length === 0 && (
                        <Typography sx={{ fontSize: '0.85rem', color: '#8a9088' }}>No actions recorded yet.</Typography>
                    )}

                    {!loading && entries.map((entry) => {
                        const meta = actionMeta[entry.action] || { label: entry.action, color: ink, bg: '#eef0ee', icon: <HistoryIcon fontSize="small" /> };
                        return (
                            <Box
                                key={entry.id}
                                display="flex"
                                gap={1.5}
                                mb={1.5}
                                sx={{ backgroundColor: 'white', border: `1px solid ${line}`, borderRadius: '6px', padding: '12px 14px' }}
                            >
                                <Box
                                    display="flex" alignItems="center" justifyContent="center"
                                    sx={{ width: 32, height: 32, borderRadius: '6px', backgroundColor: meta.bg, color: meta.color, flexShrink: 0 }}
                                >
                                    {meta.icon}
                                </Box>
                                <Box flexGrow={1} minWidth={0}>
                                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: meta.color }}>
                                            {meta.label}
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.72rem', color: '#8a9088', flexShrink: 0 }}>
                                            {moment(entry.actionDate).format('MMM D, YYYY h:mm A')}
                                        </Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: '0.8rem', color: ink, fontFamily: mono }}>
                                        {entry.actionByEmail}
                                    </Typography>

                                    {entry.action === 'Emailed' && (
                                        <Box mt={0.5}>
                                            <Typography sx={{ fontSize: '0.78rem', color: '#5b625b' }}>
                                                To: {entry.toEmails || '—'}
                                            </Typography>
                                            {entry.ccEmails && (
                                                <Typography sx={{ fontSize: '0.78rem', color: '#5b625b' }}>
                                                    Cc: {entry.ccEmails}
                                                </Typography>
                                            )}
                                            <Typography sx={{ fontSize: '0.78rem', color: '#5b625b' }}>
                                                Subject: {entry.subject || '—'}
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.78rem', color: '#5b625b' }}>
                                                Attachment: {entry.hadAttachment ? 'Yes' : 'No'}
                                            </Typography>
                                            {entry.body && (
                                                <Box
                                                    mt={0.75}
                                                    sx={{
                                                        border: `1px solid ${line}`, borderRadius: '4px',
                                                        backgroundColor: '#fafafa', padding: '8px 10px',
                                                        fontSize: '0.78rem', color: '#5b625b',
                                                        maxHeight: 220, overflow: 'auto',
                                                        '& p': { margin: '0 0 6px' },
                                                    }}
                                                    dangerouslySetInnerHTML={{ __html: entry.body }}
                                                />
                                            )}
                                        </Box>
                                    )}

                                    {entry.action === 'Rejected' && (
                                        <Box mt={0.5}>
                                            <Typography sx={{ fontSize: '0.78rem', color: '#5b625b' }}>
                                                Reason: {entry.rejectReason || '—'}
                                            </Typography>
                                            {entry.rejectComments && (
                                                <Typography sx={{ fontSize: '0.78rem', color: '#5b625b' }}>
                                                    Comments: {entry.rejectComments}
                                                </Typography>
                                            )}
                                        </Box>
                                    )}

                                    {entry.action === 'Downloaded' && entry.downloadReason && (
                                        <Box mt={0.5}>
                                            <Typography sx={{ fontSize: '0.78rem', color: '#5b625b' }}>
                                                Reason: {entry.downloadReason}
                                            </Typography>
                                            {entry.downloadComments && (
                                                <Typography sx={{ fontSize: '0.78rem', color: '#5b625b' }}>
                                                    Comments: {entry.downloadComments}
                                                </Typography>
                                            )}
                                        </Box>
                                    )}

                                    {entry.action === 'Mailed' && (
                                        <Box mt={0.5}>
                                            <Typography sx={{ fontSize: '0.78rem', color: '#5b625b' }}>
                                                Sent manually outside WebView (e.g. via Outlook)
                                            </Typography>
                                            {entry.mailedComments && (
                                                <Typography sx={{ fontSize: '0.78rem', color: '#5b625b' }}>
                                                    Comments: {entry.mailedComments}
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </Drawer>
    );
};

export default ViewAuditDrawer;
